import { type StudySwimlaneSelectDto, type StudyThreadSelectDto } from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { ConfirmationModal } from '@components/shared/confirm-delete-modal.component';
import { ModalSteps, type StepConfig, useSteps } from '@components/shared/modal-steps.component';
import { errorToast, successToast } from '@hooks/toast.hook';
import { type FC, useState } from 'react';
import { MeiaSwimlaneMeasuresForm } from './meia-swimlane-measures-form.component';
import { MeiaSwimlaneStageForm } from './meia-swimlane-stage-form.component';

type MeiaThreadStageModalProps = {
  canEdit: boolean;
  isOpen: boolean;
  missionId: string;
  onClose: () => void;
  studyId: string;
  swimlane: StudySwimlaneSelectDto;
  thread: StudyThreadSelectDto;
};

enum StageStep {
  DETAILS = 0,
  MEASURES = 1,
}

const stageStepData: StepConfig[] = [
  {
    guidance: 'Operational purpose and entry/exit criteria for this Stage.',
    label: 'Details',
    step: StageStep.DETAILS,
  },
  {
    guidance: 'Measures of Success and Effectiveness scored on this Stage.',
    label: 'Measures',
    step: StageStep.MEASURES,
  },
];

export const MeiaThreadStageModal: FC<MeiaThreadStageModalProps> = (props) => {
  const { canEdit, isOpen, missionId, onClose, studyId, swimlane, thread } = props;
  // Edit context: both steps are freely navigable from the start through the step bar, so
  // per-step `isValid` is intentionally left untracked — ModalSteps renders off `state` only,
  // and updateStepState would auto-advance the wizard, which an edit modal must not do.
  const { step, stepsInfo, handleStepClick } = useSteps(stageStepData, StageStep.DETAILS, true);
  const [isConfirmingClose, setIsConfirmingClose] = useState(false);

  return (
    <MeiaSwimlaneStageForm
      canEdit={canEdit}
      onDeleted={onClose}
      silentSuccess
      studyId={studyId}
      swimlane={swimlane}
      thread={thread}
    >
      {(detailsApi) => (
        <MeiaSwimlaneMeasuresForm
          canEdit={canEdit}
          missionId={missionId}
          silentSuccess
          swimlaneId={swimlane.id}
        >
          {(measuresApi) => {
            const isSaving = detailsApi.isUpdating || measuresApi.isSaving;

            // Guard close paths (button, overlay, Escape) when either step has unsaved edits.
            const requestClose = (): void => {
              if (detailsApi.getIsDirty() || measuresApi.getIsDirty()) {
                setIsConfirmingClose(true);
                return;
              }
              onClose();
            };

            // Validate the step being left before moving; block (and surface errors)
            // when it is invalid, like product-form-modal's handleStepClickWithValidation.
            const handleStepClickWithValidation = (targetStep: number): void => {
              if (targetStep === step) return;
              const isCurrentValid = step === StageStep.DETAILS ? detailsApi.validate() : measuresApi.validate();
              if (!isCurrentValid) {
                errorToast({ description: 'Please complete the required fields before continuing.' });
                return;
              }
              handleStepClick(targetStep);
            };

            // Save persists the whole Stage (both steps): validate each, then save every
            // dirty step and close. A failed step jumps into view and keeps the modal open.
            const handleSave = async (): Promise<void> => {
              if (!detailsApi.validate()) {
                if (step !== StageStep.DETAILS) handleStepClick(StageStep.DETAILS);
                errorToast({ description: 'Please complete the required fields before saving.' });
                return;
              }
              if (!measuresApi.validate()) {
                if (step !== StageStep.MEASURES) handleStepClick(StageStep.MEASURES);
                errorToast({ description: 'Please complete the required fields before saving.' });
                return;
              }
              try {
                let didSave = false;
                if (detailsApi.getIsDirty()) {
                  await detailsApi.submit();
                  didSave = true;
                }
                if (measuresApi.getIsDirty()) {
                  await measuresApi.submit();
                  didSave = true;
                }
                // The sub-forms stay silent (silentSuccess) so the whole save shows one toast.
                if (didSave) successToast({ description: 'Stage updated.' });
                onClose();
              } catch {
                // The failing step surfaced its error toast; keep the modal open.
              }
            };

            return (
              <>
                <Modal
                  centered
                  footer={
                    canEdit
                      ? {
                          ...(step === StageStep.DETAILS && { leftButton: detailsApi.deleteButton }),
                          rightButton: {
                            children: isSaving ? 'Saving...' : 'Save',
                            disabled: isSaving || measuresApi.isLoading,
                            onClick: () => void handleSave(),
                          },
                        }
                      : undefined
                  }
                  header={{ title: `Edit Stage · ${swimlane.phaseName}` }}
                  isOpen={isOpen}
                  onClose={requestClose}
                  width="720px"
                >
                  <div className="flex flex-col gap-4 py-2">
                    <ModalSteps
                      onStepClick={handleStepClickWithValidation}
                      stepsInfo={stepsInfo}
                    />
                    {step === StageStep.DETAILS ? detailsApi.formBody : measuresApi.measuresBody}
                  </div>
                </Modal>
                <ConfirmationModal
                  cancelLabel="Keep Editing"
                  confirmLabel="Discard Changes"
                  description="Closing now will discard the unsaved Stage edits on either step."
                  isOpen={isConfirmingClose}
                  onConfirm={onClose}
                  setIsOpen={setIsConfirmingClose}
                  title="Discard unsaved changes?"
                />
              </>
            );
          }}
        </MeiaSwimlaneMeasuresForm>
      )}
    </MeiaSwimlaneStageForm>
  );
};
